import clsx from "clsx";
import type React from "react";
import { formatSecondsToMMSS } from "../../utils/timerUtils";
import { useTimer } from "../../hooks/useTimer";
import styles from "./TimerCircle.module.css";
import type { TestDataProps } from "@repo/ui";

export type TimerCircleProps = {
  /** Initial countdown timer value in seconds */
  timerSeconds: number;
  /** Callback invoked when timer reaches 0 seconds */
  onTimerEnd?: () => void;
  /** Optional custom class name */
  className?: string | string[];
  /** ISO timestamp when timer was created (e.g., "2024-03-13T10:00:00Z") */
  createdAt?: Date | string;
  /** Total duration in seconds (used to calculate progress from elapsed time) */
  totalSeconds?: number;
} & TestDataProps;

const CIRCLE_CIRCUMFERENCE = 251.33; // 2πr where r=40

/**
 * TimerCircle Component
 * Displays a circular countdown timer with MM:SS format text overlay.
 * The circle starts full and shrinks smoothly with stroke-dashoffset animation on each second.
 *
 * Timer logic is fully encapsulated - component manages its own countdown state.
 *
 * For long timers (1+ hour), uses non-linear progress to ensure visible animation.
 */
export const TimerCircle: React.FC<TimerCircleProps> = ({
  timerSeconds,
  onTimerEnd,
  className,
  createdAt,
  totalSeconds,
  "data-testid": testId,
}) => {
  const { secondsLeft, progress: timerProgress } = useTimer(
    timerSeconds,
    true,
    onTimerEnd,
    createdAt,
    totalSeconds
  );

  // Calculate progress based on elapsed time if createdAt and totalSeconds provided
  const getElapsedProgress = (): number => {
    if (!createdAt || !totalSeconds) {
      return timerProgress;
    }

    const createdAtTime = new Date(createdAt).getTime();
    const currentTime = Date.now();
    const elapsedSeconds = Math.floor((currentTime - createdAtTime) / 1000);
    const elapsedPercentage = (elapsedSeconds / totalSeconds) * 100;

    return Math.min(100, Math.max(0, elapsedPercentage));
  };

  const progress = getElapsedProgress();

  // Calculate visual progress with non-linear curve for better visibility on long timers
  // For timers > 10 minutes, use exponential curve to show more progress early
  // For shorter timers, keep linear progress for smooth animation
  const getVisualProgress = (
    linearProgress: number,
    durationSeconds: number
  ): number => {
    if (durationSeconds <= 600) {
      // 10 minutes or less - use linear
      return linearProgress;
    }

    // For longer timers, use exponential curve: progress^0.7
    // This makes early progress more visible while maintaining smooth end game
    const normalizedProgress = linearProgress / 100;
    const visualProgress = normalizedProgress ** 0.7 * 100;

    // Ensure minimum 2% progress is always visible for UX
    return Math.max(visualProgress, 2);
  };

  const durationSeconds = totalSeconds || timerSeconds;
  const visualProgress = getVisualProgress(progress, durationSeconds);
  const strokeDashoffset = CIRCLE_CIRCUMFERENCE * (visualProgress / 100);

  // Calculate urgency color based on remaining time
  const getUrgencyColor = (
    secondsLeft: number,
    durationSeconds: number
  ): string => {
    const timeRatio = secondsLeft / durationSeconds;

    if (timeRatio > 0.5) return "#0064fa"; // Normal - blue
    if (timeRatio > 0.25) return "#f59e0b"; // Warning - orange
    return "#ef4444"; // Urgent - red
  };

  const strokeColor = getUrgencyColor(secondsLeft, durationSeconds);

  return (
    <div className={clsx(styles.container, className)} data-testid={testId}>
      <svg
        className={styles.svg}
        viewBox="0 0 88 88"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label={`Timer: ${formatSecondsToMMSS(secondsLeft)}`}
      >
        <title>Countdown Timer</title>
        {/* Background circle (full) */}
        <circle
          cx="44"
          cy="44"
          r="40"
          className={styles["circle-background"]}
        />
        {/* Progress circle with animation */}
        <circle
          cx="44"
          cy="44"
          r="40"
          className={styles["circle-progress"]}
          style={{
            strokeDashoffset,
            stroke: strokeColor,
          }}
        />
      </svg>

      {/* Text overlay - MM:SS format */}
      <div className={styles.text}>{formatSecondsToMMSS(secondsLeft)}</div>
    </div>
  );
};
